import type { ReactNode } from "react";
import { Group, Stack, Textarea } from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import { Alert, Button, SectionCard, spacing } from "@xplatform/design-system";
import { useTranslation } from "react-i18next";
import type { ReviewDecisionVerb } from "@models/review";

interface DecisionCardProps {
  reason: string;
  onReasonChange: (reason: string) => void;
  /** i18n key for a client-side validation message, or null. */
  validationKey: string | null;
  /** i18n key for a decision error (e.g. 409), or null. */
  errorKey: string | null;
  /** Which decision is currently submitting, or null. */
  submitting: ReviewDecisionVerb | null;
  onDecide: (decision: ReviewDecisionVerb) => void;
  /** The 1-5 score panel, shown first in the same card (one decision step). */
  children?: ReactNode;
}

/**
 * The Approve / Reject decision card. A reason textarea (required on reject,
 * enforced by the caller and by the API's 422/409), and the two decision
 * buttons. Separated from the Q1 panel so the sidebar sub-steps (Q1 /
 * Decision) map cleanly onto the two cards.
 */
export function DecisionCard({
  reason,
  onReasonChange,
  validationKey,
  errorKey,
  submitting,
  onDecide,
  children,
}: DecisionCardProps) {
  const { t } = useTranslation();

  return (
    <SectionCard title={t("review.decide.sectionTitle")}>
      <Stack gap={spacing.md}>
        {children}
        <Textarea
          label={t("review.decide.reasonLabel")}
          description={t("review.decide.reasonHelp")}
          placeholder={t("review.decide.reasonPlaceholder")}
          value={reason}
          onChange={(e) => onReasonChange(e.currentTarget.value)}
          autosize
          minRows={2}
          maxRows={6}
        />

        {validationKey && (
          <Alert color="yellow" title={t("review.decide.checkTitle")}>
            {t(validationKey)}
          </Alert>
        )}
        {errorKey && (
          <Alert color="rapidRed" title={t("review.errors.title")}>
            {t(errorKey)}
          </Alert>
        )}

        <Group gap={spacing.sm}>
          <Button
            color="green"
            leftSection={<IconCheck size={16} />}
            loading={submitting === "approve"}
            disabled={submitting != null}
            onClick={() => onDecide("approve")}
          >
            {t("review.decide.approve")}
          </Button>
          <Button
            color="rapidRed"
            leftSection={<IconX size={16} />}
            loading={submitting === "reject"}
            disabled={submitting != null}
            onClick={() => onDecide("reject")}
          >
            {t("review.decide.reject")}
          </Button>
        </Group>
      </Stack>
    </SectionCard>
  );
}
